import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Content, ContentWrapper, Side } from './product-create-own-order.styles';
import { FormCreateOrder } from './form-create-order/form-create-order';
import { FormProductInfo } from './form-product-info/form-product-info';
import { FormRenderProps } from 'react-final-form';
import { CreateOrderFormFields } from './form-create-order/types';
import { queryParametersFromString } from '@shared/lib/utils/create-query-parameters/query-parameters-from-string';
import { useHistory, useLocation } from 'react-router-dom';
import { ProductCreateOrderProps } from './types';
import { useProductShortInfoSharedQuery } from '@entities/product/libs/hooks/use-product-short-info-shared-query/use-product-short-info-shared-query';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { useOrganizationsDetailSharedQuery } from '@entities/organization/libs/hooks/use-organization-detail-shared-query/use-organization-detail-shared-query';
import { useCurrentUserSelector } from '@entities/current-user/model/selectors';
import { useCreateOwnOrderMutation } from '../api/order-create-api';
import { CACHE_KEY_CREATE_OWN_ORDER } from '../config/api-constants';
import { ROUTE_ADMIN_APPROVE_ORDERS } from '@shared/config/routes/routes';
import toast from 'react-hot-toast';
import { Toast } from '@shared/ui/toast/toast';
import { NetworkStateNew } from '@shared/ui/network-state-new/network-state-new';
import { useRedirect } from '@shared/lib/hooks/use-redirect/use-redirect';

/* компонент для приоберетния платного продукта сотрудником */
export const ProductCreateOwnOrder: React.FC<ProductCreateOrderProps> = ({ productId }) => {
    const redirect = useRedirect();
    const location = useLocation();
    const history = useHistory();
    const handleSubmitRef = useRef<
        FormRenderProps<CreateOrderFormFields, Partial<CreateOrderFormFields>>['handleSubmit'] | null
    >(null);
    const user = useCurrentUserSelector();

    const { count: countFromSearch } = queryParametersFromString(location.search);
    const [productCount, setProductCount] = useState(Number(countFromSearch) || 1);

    const [getProduct, getProductResult] = useProductShortInfoSharedQuery(productId);
    const [, , handlePrefetchOrganization] = useOrganizationsDetailSharedQuery(user?.company?.id || 0);
    const [, createOwnOrderResult] = useCreateOwnOrderMutation({ fixedCacheKey: CACHE_KEY_CREATE_OWN_ORDER });

    const productError = getQueryError(getProductResult);

    const updateProductCount = (newCount: number) => {
        history.replace(`?count=${newCount}`);
        setProductCount(newCount);
    };

    useLayoutEffect(() => {
        getProduct(productId);
    }, [productId]);

    useLayoutEffect(() => {
        handlePrefetchOrganization();
    }, []);

    useEffect(() => {
        const product = createOwnOrderResult?.data?.product;
        const isFree = product && Number(product.price) === 0;
        if (createOwnOrderResult.isSuccess) {
            toast.custom((t) => {
                return (
                    <Toast
                        type='success'
                        title='Успешно!'
                        description={isFree ? 'Продукт успешно приобретен' : 'Сделка успешно создана'}
                        onClose={() => {
                            toast.remove(t.id);
                        }}
                    />
                );
            });
            createOwnOrderResult.reset();
            redirect({ url: ROUTE_ADMIN_APPROVE_ORDERS });
        } else if (createOwnOrderResult.isError) {
            const errorMessage = getQueryError(createOwnOrderResult)?.message;
            toast.custom((t) => {
                return (
                    <Toast
                        type='error'
                        title='Ошибка!'
                        description={`Возникла ошибка при отправке запроса.\n${errorMessage}`}
                        onClose={() => {
                            toast.remove(t.id);
                        }}
                    />
                );
            });
            createOwnOrderResult.reset();
        }
    }, [createOwnOrderResult.isSuccess, createOwnOrderResult.isError]);

    return (
        <ContentWrapper>
            <NetworkStateNew
                isLoading={
                    getProductResult.isFetching || getProductResult.isFetching || getProductResult.isUninitialized
                }
                isInitialized={Boolean(getProductResult.data)}
                isError={Boolean(productError)}
                isFiltered={false}
                hasData={Boolean(getProductResult.data)}
                errorProps={{
                    title: `Не удалось найти продукт с ID ${productId}`,
                    description: productError?.message,
                }}
                emptyProps={{
                    title: `Не удалось найти продукт с ID ${productId}`,
                }}>
                <>
                    <Content>
                        <FormCreateOrder
                            quantity={productCount}
                            productId={productId}
                            handleSubmitRef={handleSubmitRef}
                        />
                    </Content>
                    <Side>
                        <FormProductInfo
                            productId={productId}
                            productCount={productCount}
                            handleSubmitRef={handleSubmitRef}
                            onProductCountChange={updateProductCount}
                        />
                    </Side>
                </>
            </NetworkStateNew>
        </ContentWrapper>
    );
};
